export const STUDENT_BEHAVIORAL_TAGS = [
  'disciplined',
  'needy',
  'motivation_dependent',
  'disengaged',
] as const;

export type StudentBehavioralTag = (typeof STUDENT_BEHAVIORAL_TAGS)[number];

export const STUDENT_BEHAVIORAL_PILLARS = [
  'training_completion',
  'feedback_submission',
  'app_engagement',
  'message_responsiveness',
  'consistency',
] as const;

export type StudentBehavioralPillar = (typeof STUDENT_BEHAVIORAL_PILLARS)[number];

export const STUDENT_BEHAVIORAL_PILLAR_POINTS = 20;

export type StudentBehavioralPillars = Record<StudentBehavioralPillar, boolean>;

export const STUDENT_EXIT_RISK_OPTIONS = ['low', 'medium', 'high'] as const;

export type StudentExitRisk = (typeof STUDENT_EXIT_RISK_OPTIONS)[number];

export const STUDENT_EXIT_REVERSIBLE_OPTIONS = ['no', 'maybe', 'yes'] as const;

export type StudentExitReversible = (typeof STUDENT_EXIT_REVERSIBLE_OPTIONS)[number];

export interface StudentBehavioralProfile {
  studentId: number;
  tags: StudentBehavioralTag[];
  pillars: StudentBehavioralPillars;
  score: number;
  exitRisk: StudentExitRisk | null;
  reversible: StudentExitReversible | null;
  updatedAt: string | null;
}

export const STUDENT_BEHAVIORAL_TAG_LABELS: Record<StudentBehavioralTag, string> = {
  disciplined: 'Disciplinada',
  needy: 'Carente',
  motivation_dependent: 'Dependente de motivação',
  disengaged: 'Desengajada',
};

export const STUDENT_BEHAVIORAL_PILLAR_LABELS: Record<StudentBehavioralPillar, string> = {
  training_completion: 'Cumprimento dos treinos',
  feedback_submission: 'Envio de feedback',
  app_engagement: 'Engajamento no app',
  message_responsiveness: 'Resposta a mensagens',
  consistency: 'Consistência',
};

export const STUDENT_BEHAVIORAL_TAG_APPROACH: Record<StudentBehavioralTag, string> = {
  disciplined: 'Reconhecer evolução, desafiar, mostrar progressão e reforçar performance.',
  needy: 'Respostas mais calorosas, elogiar pequenas vitórias, chamar pelo nome e manter pequenas interações no ciclo.',
  motivation_dependent: 'Entrar em contato antes de ela desaparecer, lembrar o objetivo, reduzir barreiras e negociar metas menores.',
  disengaged: 'Descobrir o motivo (financeiro, maternidade, trabalho, doença, hábito ou confiança) e adaptar a conversa.',
};

/** Ordem do guia de abordagem na aba Perfil comportamental. */
export const STUDENT_BEHAVIORAL_TAG_GUIDE_ORDER: StudentBehavioralTag[] = [
  'disciplined',
  'motivation_dependent',
  'needy',
  'disengaged',
];

export type StudentBehavioralTagTone = 'success' | 'warning' | 'warm' | 'danger';

export const STUDENT_EXIT_RISK_LABELS: Record<StudentExitRisk, string> = {
  low: 'Baixo',
  medium: 'Médio',
  high: 'Alto',
};

export const STUDENT_EXIT_REVERSIBLE_LABELS: Record<StudentExitReversible, string> = {
  no: 'Não',
  maybe: 'Talvez',
  yes: 'Sim',
};

export const STUDENT_BEHAVIORAL_TAG_TONE: Record<StudentBehavioralTag, StudentBehavioralTagTone> = {
  disciplined: 'success',
  motivation_dependent: 'warning',
  needy: 'warm',
  disengaged: 'danger',
};

export const emptyStudentBehavioralPillars = (): StudentBehavioralPillars => ({
  training_completion: false,
  feedback_submission: false,
  app_engagement: false,
  message_responsiveness: false,
  consistency: false,
});

export const studentBehavioralProfileScore = (pillars: StudentBehavioralPillars): number => (
  STUDENT_BEHAVIORAL_PILLARS.filter((pillar) => pillars[pillar]).length * STUDENT_BEHAVIORAL_PILLAR_POINTS
);
